import {
  Amount,
  Box,
  Button,
  EditIcon,
  Link,
  SparklesIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TrashIcon,
} from '@razorpay/blade/components';
import React from 'react';
import ComponentContainer from '../components/generics/ComponentContainer';
import ComponentHeader from '../components/generics/ComponentHeader';
import ComponentSection from '../components/generics/ComponentSection';
import ComponentSectionItem from '../components/generics/ComponentSectionItem';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';

type PerquisiteData = {
  id: string;
  component: string;
  amountPaidByEmployee: number;
  totalValue: number;
};

const PerquisitesBreakdown = ({
  openPerquisiteModal,
  openDeleteModal,
  setSelectedComponentId,
  perquisitesData,
}: {
  openPerquisiteModal: () => void;
  openDeleteModal: () => void;
  setSelectedComponentId: (id: string) => void;
  perquisitesData: PerquisiteData[];
}) => {
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);

  const onAddNewPerquisite = () => {
    setSelectedComponentId('');
    openPerquisiteModal();
  };

  const onEdit = (id: string) => {
    // TODO: Get the perquisite form data from the API
    setSelectedComponentId(id);
    openPerquisiteModal();
  };

  const onDelete = (id: string) => {
    setSelectedComponentId(id);
    openDeleteModal();
  };

  return (
    <>
      <ComponentContainer>
        <ComponentSection>
          <ComponentHeader
            icon={<SparklesIcon size="medium" color="surface.icon.primary.normal" />}
            iconBackground={'surface.background.primary.subtle'}
            title={'Perquisites'}
            description={'Perks that employees enjoy over and above their salary'}
            button={
              hasPayrollEditPermission ? (
                <Button
                  variant="tertiary"
                  color="white"
                  marginLeft={'auto'}
                  onClick={onAddNewPerquisite}>
                  + New Perquisite
                </Button>
              ) : null
            }
          />
          <ComponentSectionItem>
            <Box borderRadius={'large'} overflow={'hidden'}>
              <Table
                data={{
                  nodes: perquisitesData,
                }}
                backgroundColor="transparent">
                {(data) => (
                  <>
                    <TableHeader>
                      <TableHeaderRow>
                        <TableHeaderCell>
                          <Text weight="semibold">Component</Text>
                        </TableHeaderCell>
                        <TableHeaderCell textAlign="right">Amount Paid by Employee</TableHeaderCell>
                        <TableHeaderCell textAlign="right">Total Value (Annual)</TableHeaderCell>
                        {hasPayrollEditPermission ? (
                          <TableHeaderCell textAlign="right">
                            <Text weight="semibold">Action</Text>
                          </TableHeaderCell>
                        ) : null}
                      </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                      {data.map((row) => (
                        <TableRow key={row.id} item={row}>
                          <TableCell>
                            <Text>{row.component}</Text>
                          </TableCell>
                          <TableCell textAlign="right">
                            <Amount value={row.amountPaidByEmployee} />
                          </TableCell>
                          <TableCell textAlign="right">
                            <Amount value={row.totalValue} />
                          </TableCell>
                          {hasPayrollEditPermission ? (
                            <TableCell textAlign="right">
                              <Box display="flex" gap="spacing.5">
                                <Link icon={EditIcon} onClick={() => onEdit(row.id)} testID={`edit-${row.id}`} />
                                <Link icon={TrashIcon} onClick={() => onDelete(row.id)} testID={`delete-${row.id}`} />
                              </Box>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
            </Box>
          </ComponentSectionItem>
        </ComponentSection>
      </ComponentContainer>
    </>
  );
};

export default PerquisitesBreakdown; 